import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Check, Copy, Eye } from 'lucide-react'
import { cn } from '@/lib/cn'
import { useT } from '@/i18n'

/* ═══ RelativeTime ══════════════════════════════════════════════
   SKILL §6: nisbiy ("3 soat oldin"), hover → to'liq ISO + UTC.
   X4: foydalanuvchi vaqt mintaqasida, yonida UTC. */

export function RelativeTime({ value, className }: { value: string | number | Date | null | undefined; className?: string }) {
  const t = useT()
  if (value === null || value === undefined || value === '') {
    return <span className={cn('text-tertiary', className)}>—</span>
  }

  const d = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(d.getTime())) return <span className={cn('text-tertiary', className)}>—</span>

  const diff = Date.now() - d.getTime()
  const abs = Math.abs(diff)
  const sec = Math.round(abs / 1000)
  const min = Math.round(sec / 60)
  const hr = Math.round(min / 60)
  const day = Math.round(hr / 24)
  const mon = Math.round(day / 30)
  const yr = Math.round(day / 365)

  let label: string
  if (sec < 45) label = t.time.justNow
  else if (min < 60) label = t.time.minutesAgo(min)
  else if (hr < 24) label = t.time.hoursAgo(hr)
  else if (day < 30) label = t.time.daysAgo(day)
  else if (mon < 12) label = t.time.monthsAgo(mon)
  else label = t.time.yearsAgo(yr)

  // Mahalliy vaqt + UTC — solishtirish uchun ikkalasi kerak.
  const title = `${d.toLocaleString()}\nUTC: ${d.toISOString()}`

  return (
    <time dateTime={d.toISOString()} title={title} className={cn('whitespace-nowrap', className)}>
      {label}
    </time>
  )
}

/* ═══ CopyButton ════════════════════════════════════════════════ */

export function CopyButton({ value, className, label }: { value: string; className?: string; label?: string }) {
  const t = useT()
  const [done, setDone] = useState(false)
  const timer = useRef<number | undefined>(undefined)

  useEffect(() => () => window.clearTimeout(timer.current), [])

  async function copy() {
    try {
      await navigator.clipboard.writeText(value)
    } catch {
      // Clipboard API HTTPS'siz yoki ruxsatsiz ishlamaydi — eski usul.
      const ta = document.createElement('textarea')
      ta.value = value
      ta.style.position = 'fixed'
      ta.style.opacity = '0'
      document.body.appendChild(ta)
      ta.select()
      document.execCommand('copy')
      ta.remove()
    }
    setDone(true)
    window.clearTimeout(timer.current)
    timer.current = window.setTimeout(() => setDone(false), 1400)
  }

  return (
    <button
      type="button"
      onClick={copy}
      title={done ? t.common.copied : (label ?? t.common.copy)}
      aria-label={done ? t.common.copied : (label ?? t.common.copy)}
      className={cn(
        'inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-sm',
        'text-tertiary hover:bg-surface-3 hover:text-primary transition-colors',
        className,
      )}
    >
      {done ? <Check size={12} className="text-success" /> : <Copy size={12} />}
    </button>
  )
}

/* ═══ MonoValue — ID / token / hash / IP ════════════════════════
   SKILL §3: bu qiymatlar ko'z bilan solishtiriladi → mono majburiy. */

export function MonoValue({
  value,
  copyable = true,
  truncate,
  className,
}: {
  value: string
  copyable?: boolean
  /** Uzun hash uchun: boshidan/oxiridan N belgi qoldiradi. */
  truncate?: number
  className?: string
}) {
  const shown =
    truncate && value.length > truncate * 2 + 1
      ? `${value.slice(0, truncate)}…${value.slice(-truncate)}`
      : value

  return (
    <span className={cn('group inline-flex items-center gap-1', className)}>
      <span className="font-mono text-sm text-secondary" title={shown !== value ? value : undefined}>
        {shown}
      </span>
      {copyable && <CopyButton value={value} className="opacity-0 group-hover:opacity-100 focus:opacity-100" />}
    </span>
  )
}

/* ═══ PIIField ══════════════════════════════════════════════════
   SKILL §6 / rbac-ui.md: default'da maskalangan. Ochish — ONGLI harakat,
   auditga yoziladi, 5 daqiqadan keyin qayta maskalanadi.

   MASKALASH ALGORITMI — TASDIQLANGAN (foydalanuvchi, 2026-07-19):
   «TZ misoliga sodiq» varianti tanlandi → TZ §1.3 dagi `+998**###4567`
   AYNAN qayta ishlab chiqariladi.

   Format tasodifiy emas — u UZ raqamining guruhlanishini saqlaydi:
       +998 90 123 45 67   →   +998 ** ### 4567
            └┬┘ └┬┘ └──┬─┘         └┬┘ └┬┘ └─┬┘
        operator │   oxirgi 4       │   │    ochiq
              keyingi 3          `*`  `#`
   Ya'ni `*` = operator kodi guruhi (2 raqam), `#` = keyingi guruh (3 raqam).
   HOLAT.md avval buni «ichkaridan nomuvofiq» degan edi — bu baho XATO edi.

   ⚠️ TZ FAQAT TELEFON uchun qoida beradi. IP / qurilma / geo / ism uchun
   qoida YO'Q va bu ataylab to'ldirilmagan (siyosatni o'ylab topish emas).
   Hozir bu turlar uchun ustun ham yo'q — paydo bo'lganda qoida so'raladi.
   tz/admin/HOLAT.md §3.2 ga qara. */

export type PiiLevel = 'L2' | 'L3' | 'L4'

/**
 * E.164 davlat kodi uzunligi (1–3).
 *
 * Buni 3 deb qattiq yozib qo'yish XATO bo'lardi: MyGram 122 davlatni
 * qo'llab-quvvatlaydi (`countries` jadvali), va `+7` (RU/KZ) / `+1` (US/CA)
 * bir raqamli. Noto'g'ri uzunlik maskani noto'g'ri joydan kesadi —
 * ya'ni yopilishi kerak raqam ochilib qoladi.
 *
 * Zona 1 va 7 — bir raqamli. Quyidagi to'plam — ikki raqamli kodlar
 * (ITU-T E.164 taqsimoti). Qolgani — uch raqamli.
 */
const CC2 = new Set([
  '20', '27',
  '30', '31', '32', '33', '34', '36', '39',
  '40', '41', '43', '44', '45', '46', '47', '48', '49',
  '51', '52', '53', '54', '55', '56', '57', '58',
  '60', '61', '62', '63', '64', '65', '66',
  '81', '82', '84', '86',
  '90', '91', '92', '93', '94', '95', '98',
])

function ccLength(digits: string): number {
  const first = digits.slice(0, 1)
  if (first === '1' || first === '7') return 1
  if (CC2.has(digits.slice(0, 2))) return 2
  return 3
}

/**
 * @param phone     E.164 raqam (`+998901234567`)
 * @param dialCode  Ma'lum bo'lsa — aniq davlat kodi (`+998`). `countries`
 *                  jadvalidan kelganda taxmin qilish shart emas.
 */
export function maskPhone(phone: string, dialCode?: string): string {
  const digits = phone.replace(/\D/g, '')
  // Juda qisqa raqam — guruhlash ma'nosiz, butunlay yopiladi.
  if (digits.length < 8) return '*'.repeat(Math.max(4, digits.length))

  const known = dialCode?.replace(/\D/g, '')
  const ccLen = known && digits.startsWith(known) ? known.length : ccLength(digits)

  const cc = digits.slice(0, ccLen) // davlat kodi ochiq (TZ: `+998`)
  const tail = digits.slice(-4) // oxirgi 4 ochiq (TZ: `4567`)
  const hidden = digits.length - ccLen - 4
  if (hidden <= 0) return `${phone.trim().startsWith('+') ? '+' : ''}${cc}${'*'.repeat(4)}`

  // Birinchi 2 yopiq raqam `*` (UZ da — operator kodi guruhi), qolgani `#`.
  // UZ (998 + 9 raqam): hidden=5 → `**` + `###` = TZ misoli bilan bayt-mos.
  // Boshqa davlatlarda guruh uzunligi bizga ma'lum emas — 2+qolgani bo'lib
  // degradatsiya qiladi, lekin ochiq/yopiq CHEGARASI to'g'ri qoladi.
  const stars = '*'.repeat(Math.min(2, hidden))
  const hashes = '#'.repeat(Math.max(0, hidden - 2))

  const head = phone.trim().startsWith('+') ? `+${cc}` : cc
  return `${head}${stars}${hashes}${tail}`
}

export function maskEmail(email: string): string {
  // TZ email uchun format bermaydi. Telefon bilan BIR XIL alifbo (`*`)
  // ishlatiladi — ikki xil belgi (`•` va `*`) bir jadvalda ikki xil qoida
  // borday ko'rinadi (skill §1: interfeys bitta tizim bo'lib o'qilsin).
  const [user = '', domain = ''] = email.split('@')
  if (!domain) return '*'.repeat(6)
  const visible = user.slice(0, 1)
  return `${visible}${'*'.repeat(Math.max(2, user.length - 1))}@${domain}`
}

export function PIIField({
  value,
  masked,
  level = 'L2',
  onReveal,
  className,
}: {
  value: string
  /** Tayyor maska. Berilmasa — telefon/email avtomatik aniqlanadi. */
  masked?: string
  level?: PiiLevel
  /**
   * Ochish so'ralganda chaqiriladi. AUDITGA YOZISH shu yerda bo'ladi.
   * `false` qaytarsa — ochilmaydi (L3 sabab so'raladi, L4 four-eyes).
   */
  onReveal?: () => boolean | Promise<boolean>
  className?: string
}) {
  const t = useT()
  const [revealed, setRevealed] = useState(false)
  const timer = useRef<number | undefined>(undefined)

  useEffect(() => () => window.clearTimeout(timer.current), [])

  const maskedValue = masked ?? (value.includes('@') ? maskEmail(value) : maskPhone(value))

  async function reveal() {
    const ok = onReveal ? await onReveal() : true
    if (!ok) return
    setRevealed(true)
    // rbac-ui.md: ochilgan PII 5 daqiqadan keyin qayta maskalanadi.
    window.clearTimeout(timer.current)
    timer.current = window.setTimeout(() => setRevealed(false), 5 * 60 * 1000)
  }

  if (revealed) {
    return (
      <span className={cn('inline-flex items-center gap-1', className)}>
        <span className="font-mono text-sm text-primary">{value}</span>
        <CopyButton value={value} />
        <span className="text-xs text-warning" title={t.table.piiRevealed}>
          ●
        </span>
      </span>
    )
  }

  return (
    <span className={cn('inline-flex items-center gap-1', className)}>
      <span className="font-mono text-sm text-tertiary select-none">{maskedValue}</span>
      <button
        type="button"
        onClick={reveal}
        title={`${t.table.revealPii} (${level})`}
        aria-label={`${t.table.revealPii} (${level})`}
        className="inline-flex h-5 w-5 items-center justify-center rounded-sm text-tertiary hover:bg-surface-3 hover:text-primary"
      >
        <Eye size={12} />
      </button>
    </span>
  )
}

/* ═══ RiskScore ═════════════════════════════════════════════════
   SKILL §6: 0–100 raqam + rangli chiziqcha. */

export function RiskScore({ value, reasons, className }: { value: number; reasons?: string[]; className?: string }) {
  const v = Math.max(0, Math.min(100, value))
  const bucket = v <= 20 ? 0 : v <= 40 ? 1 : v <= 60 ? 2 : v <= 80 ? 3 : 4
  const color = `var(--risk-${bucket})`

  return (
    <span
      className={cn('inline-flex items-center gap-1.5 tabular', className)}
      title={reasons?.length ? reasons.join('\n') : undefined}
    >
      <span className="font-mono text-sm" style={{ color }}>
        {v}
      </span>
      <span className="h-1 w-8 overflow-hidden rounded-full bg-surface-3" aria-hidden>
        <span className="block h-full rounded-full" style={{ width: `${v}%`, background: color }} />
      </span>
    </span>
  )
}

/* ═══ Money ═════════════════════════════════════════════════════
   SKILL §6: mono, o'ngga tekislangan, valyuta belgisi. */

export function Money({
  amount,
  currency = 'UZS',
  className,
}: {
  amount: number
  currency?: string
  className?: string
}) {
  return (
    <span className={cn('block text-right font-mono text-sm tabular', className)}>
      {amount.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
      <span className="ml-1 text-tertiary">{currency}</span>
    </span>
  )
}

/* ═══ UserCell ══════════════════════════════════════════════════
   SKILL §6: avatar 20px + nom + @username kulrang. */

export function UserCell({
  name,
  username,
  avatarUrl,
  className,
}: {
  name?: string | null
  username?: string | null
  avatarUrl?: string | null
  className?: string
}) {
  const display = name?.trim() || (username ? `@${username}` : '—')
  const initial = display.replace('@', '').charAt(0).toUpperCase() || '?'

  return (
    <span className={cn('flex min-w-0 items-center gap-2', className)}>
      {avatarUrl ? (
        <img src={avatarUrl} alt="" className="h-5 w-5 shrink-0 rounded-full object-cover" />
      ) : (
        <span
          aria-hidden
          className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-surface-3 text-[10px] font-medium text-secondary"
        >
          {initial}
        </span>
      )}
      <span className="min-w-0 truncate text-primary">{display}</span>
      {name && username && <span className="shrink-0 text-tertiary">@{username}</span>}
    </span>
  )
}

/* ═══ Planned — hali ishlamaydigan funksiya belgisi ═════════════
   B yo'li hujjatlar saytida ishlatgan qoidaning admin varianti:
   ishlamaydigan narsa «ishlaydi» deb ko'rsatilmaydi. */

export function PlannedBadge({ className }: { className?: string }) {
  const t = useT()
  return (
    <span
      title={t.common.plannedHint}
      className={cn(
        'inline-flex items-center rounded-sm border border-dashed border-border-strong',
        'px-1.5 py-0.5 text-xs text-tertiary uppercase',
        className,
      )}
    >
      {t.common.planned}
    </span>
  )
}

export function InfoRow({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <div className="flex items-baseline gap-3 py-1.5">
      <span className="w-40 shrink-0 text-sm text-tertiary">{label}</span>
      <span className="min-w-0 flex-1 text-base text-primary">{children}</span>
    </div>
  )
}
